/**
 * Hebrew-domain constants. Single source of truth used by both server and
 * client. Do not duplicate these elsewhere.
 *
 * If you need to add a parasha / holiday / book / cantor / aliyah-name,
 * edit this file and rebuild — both server and client will pick it up.
 */

// ─── Books (ספרי תורה in weekly rotation, memorial dedications) ───────────

export interface WeeklyBook {
  /** Display name in Hebrew. */
  name: string;
  /** Week-of-year (1..53) this book is dedicated to. */
  week: number;
}

export const WeeklyBooks: readonly WeeklyBook[] = [
  { name: 'ספר של מנשה אשוואל ז"ל', week: 9 },
  { name: 'ספר של  יהודה פלח ז"ל', week: 10 },
  { name: 'ספר של יגאל ודני פנחס ז"ל', week: 11 },
  { name: 'ספר של יפת בדני ז"ל', week: 12 },
  { name: 'ספר של יוסף חדד ז"ל', week: 13 },
  { name: 'ספר של יפת ויונה גדסי ז"ל', week: 14 },
  { name: 'ספר של שמעון פנחס ז"ל', week: 1 },
  { name: 'ספר של בניה ושדי ז"ל', week: 2 },
  { name: 'ספר של נפתלי לוי ז"ל', week: 3 },
  { name: 'ספר של שלום כהן ז"ל', week: 4 },
  { name: 'ספר של סעדיה ונעמה דמארי ז"ל', week: 5 },
  { name: 'ספר של לאה פאדל ובת שבע כהן ז"ל', week: 6 },
  { name: 'ספר של מזל בדני ז"ל', week: 7 },
  { name: 'ספר של בנימין שירי ושושנה בן מנשה ז"ל', week: 8 },
  { name: 'ספר של אורה גבאי ז"ל', week: 15 },
  { name: 'ספר של אילן בדני', week: 16 },
];

// ─── Cantors (חזנים) ──────────────────────────────────────────────────────

export const Hazanim: readonly string[] = [
  'יניב בדני',
  'ישראל גרמה',
  'שאול פנחס',
];

// ─── Parashot weekly + holidays ───────────────────────────────────────────

export interface ParashaOption {
  /** Latin transliteration — used in fuzzy search. */
  eng: string;
  /** Hebrew name — saved on the order document. */
  heb: string;
}

export const Parashot: readonly ParashaOption[] = [
  { eng: 'Bereshit', heb: 'בראשית' },
  { eng: 'Noach', heb: 'נח' },
  { eng: 'Lech-Lecha', heb: 'לך־לך' },
  { eng: 'Vayera', heb: 'וירא' },
  { eng: 'Chayei Sarah', heb: 'חיי שרה' },
  { eng: 'Toldot', heb: 'תולדות' },
  { eng: 'Vayetzei', heb: 'ויצא' },
  { eng: 'Vayishlach', heb: 'וישלח' },
  { eng: 'Vayeshev', heb: 'וישב' },
  { eng: 'Miketz', heb: 'מקץ' },
  { eng: 'Vayigash', heb: 'ויגש' },
  { eng: 'Vayechi', heb: 'ויחי' },
  { eng: 'Shemot', heb: 'שמות' },
  { eng: 'Vaera', heb: 'וארא' },
  { eng: 'Bo', heb: 'בא' },
  { eng: 'Beshalach', heb: 'בשלח' },
  { eng: 'Yitro', heb: 'יתרו' },
  { eng: 'Mishpatim', heb: 'משפטים' },
  { eng: 'Terumah', heb: 'תרומה' },
  { eng: 'Tetzaveh', heb: 'תצוה' },
  { eng: 'Ki Tisa', heb: 'כי תשא' },
  { eng: 'Vayakhel', heb: 'ויקהל' },
  { eng: 'Pekudei', heb: 'פקודי' },
  { eng: 'Vayikra', heb: 'ויקרא' },
  { eng: 'Tzav', heb: 'צו' },
  { eng: 'Shemini', heb: 'שמיני' },
  { eng: 'Tazria', heb: 'תזריע' },
  { eng: 'Metzora', heb: 'מצורע' },
  { eng: 'Achrei Mot', heb: 'אחרי מות' },
  { eng: 'Kedoshim', heb: 'קדושים' },
  { eng: 'Emor', heb: 'אמור' },
  { eng: 'Behar', heb: 'בהר' },
  { eng: 'Bechukotai', heb: 'בחוקותי' },
  { eng: 'Bamidbar', heb: 'במדבר' },
  { eng: 'Naso', heb: 'נשא' },
  { eng: 'Behaalotecha', heb: 'בהעלותך' },
  { eng: 'Shelach', heb: 'שלח' },
  { eng: 'Korach', heb: 'קרח' },
  { eng: 'Chukat', heb: 'חקת' },
  { eng: 'Balak', heb: 'בלק' },
  { eng: 'Pinchas', heb: 'פנחס' },
  { eng: 'Matot', heb: 'מטות' },
  { eng: 'Masei', heb: 'מסעי' },
  { eng: 'Devarim', heb: 'דברים' },
  { eng: 'Vaetchanan', heb: 'ואתחנן' },
  { eng: 'Eikev', heb: 'עקב' },
  { eng: 'Re’eh', heb: 'ראה' },
  { eng: 'Shoftim', heb: 'שופטים' },
  { eng: 'Ki Teitzei', heb: 'כי תצא' },
  { eng: 'Ki Tavo', heb: 'כי תבא' },
  { eng: 'Nitzavim', heb: 'נצבים' },
  { eng: 'Vayelech', heb: 'וילך' },
  { eng: 'Haazinu', heb: 'האזינו' },
  { eng: 'Vezot Haberakhah', heb: 'וזאת הברכה' },
];

export const Holidays: readonly ParashaOption[] = [
  { eng: 'Rosh Hashanah', heb: 'ראש השנה' },
  { eng: 'Yom Kippur', heb: 'יום כיפור' },
  { eng: 'Sukkot', heb: 'סוכות' },
  { eng: 'Shemini Atzeret', heb: 'שמיני עצרת' },
  { eng: 'Simchat Torah', heb: 'שמחת תורה' },
  { eng: 'Passover', heb: 'פסח' },
  { eng: 'Shavuot', heb: 'שבועות' },
  { eng: 'Chanukah', heb: 'חנוכה' },
  { eng: 'Purim', heb: 'פורים' },
  { eng: 'Tisha B’Av', heb: 'תשעה באב' },
  { eng: 'Lag BaOmer', heb: 'ל״ג בעומר' },
  { eng: 'Tu BiShvat', heb: 'ט״ו בשבט' },
  { eng: 'Yom HaShoah', heb: 'יום השואה' },
  { eng: 'Yom HaZikaron', heb: 'יום הזיכרון' },
  { eng: 'Yom HaAtzmaut', heb: 'יום העצמאות' },
  { eng: 'Yom Yerushalayim', heb: 'יום ירושלים' },
  { eng: 'Tzom Gedaliah', heb: 'צום גדליה' },
  { eng: 'Tu B’Av', heb: 'ט״ו באב' },
  { eng: 'Asara B’Tevet', heb: 'עשרה בטבת' },
];

/** Flat list of every Hebrew parasha / holiday name. Used for membership checks. */
export const ALL_PARASHOT_HEB: readonly string[] = [
  ...Parashot.map((p) => p.heb),
  ...Holidays.map((h) => h.heb),
];

// ─── Aliyot / kibbudim catalogue (what can be "ordered") ──────────────────

export interface OrderOption {
  /** Stored value on the order. */
  value: string;
  /** Display label in Hebrew. */
  label: string;
}

export const OrderOptions: readonly OrderOption[] = [
  { value: 'מפטיר', label: 'מפטיר' },
  { value: 'תפילה', label: 'תפילה' },
  { value: 'תפילה-ערבית', label: 'תפילה-ערבית' },
  { value: 'תפילה-שחרית', label: 'תפילה-שחרית' },
  { value: 'תפילה-מוסף', label: 'תפילה-מוסף' },
  { value: 'תפילה-מנחה', label: 'תפילה-מנחה' },
  { value: 'תרגום', label: 'תרגום' },
  { value: 'מנורה', label: 'מנורה' },
  { value: 'עליה-מנחה-ראשון', label: 'עליה-מנחה-ראשון' },
  { value: 'עליה-מנחה-שני', label: 'עליה-מנחה-שני' },
  { value: 'עליה-מנחה-שלישי', label: 'עליה-מנחה-שלישי' },
  { value: 'עליה-ראשון', label: 'עליה-ראשון' },
  { value: 'עליה-שני', label: 'עליה-שני' },
  { value: 'עליה-שלישי', label: 'עליה-שלישי' },
  { value: 'עליה-רביעי', label: 'עליה-רביעי' },
  { value: 'עליה-חמישי', label: 'עליה-חמישי' },
  { value: 'עליה-שישי', label: 'עליה-שישי' },
  { value: 'עליה-שביעי', label: 'עליה-שביעי' },
  { value: 'אזכרה', label: 'אזכרה' },
  { value: 'פתיחת היכל', label: 'פתיחת היכל' },
  { value: 'סעודה שלישית', label: 'סעודה שלישית' },
  { value: 'ארוע', label: 'ארוע' },
  { value: 'תרומה', label: 'תרומה' },
  { value: 'תשלום', label: 'תשלום' },
  { value: 'ביטוח ספר תורה', label: 'ביטוח ספר תורה' },
  { value: 'בר-יוחאי', label: 'בר-יוחאי' },
  { value: 'ברכה', label: 'ברכה' },
  { value: 'עמותה', label: 'דמי עמותה' },
  { value: 'זיכוי', label: 'זיכוי' },
  { value: 'לך-אלי', label: 'לך-אלי' },
  { value: 'סליחות-ערבית', label: 'סליחות ערבית' },
  { value: 'ספר', label: 'ספר' },
  { value: 'סליחות-שחרית', label: 'סליחות שחרית' },
  { value: 'מפטיר-שחרית', label: 'מפטיר שחרית' },
  { value: 'סליחות-מוסף', label: 'סליחות מוסף' },
  { value: 'סליחות-מנחה', label: 'סליחות מנחה' },
  { value: 'כתר-תורה', label: 'כתר תורה' },
  { value: 'הפטרת-יונה', label: 'הפטרת יונה' },
  { value: 'נעילה', label: 'נעילה' },
  { value: 'חתן-תורה', label: 'חתן-תורה' },
  { value: 'ברכה-לרבים', label: 'ברכה לרבים' },
  { value: 'הקפות', label: 'הקפות' },
];

// ─── Order option groups (the "what was purchased" picker) ────────────────
//
// Display-only grouping of OrderOptions for the multi-column picker in the
// new-order form. Values must match OrderOptions.value exactly; anything
// not listed here lands in an "אחר" group (see groupedOrderOptions), so a
// new option can never silently disappear from the picker.

export interface OrderOptionGroup {
  readonly id: string;
  readonly label: string;
  readonly values: readonly string[];
}

export const ORDER_OPTION_GROUPS: readonly OrderOptionGroup[] = [
  {
    id: 'aliyot',
    label: 'עליות לתורה',
    values: [
      'פתיחת היכל', 'עליה-ראשון', 'עליה-שני', 'עליה-שלישי', 'עליה-רביעי',
      'עליה-חמישי', 'עליה-שישי', 'עליה-שביעי', 'מפטיר', 'תרגום',
    ],
  },
  {
    id: 'mincha',
    label: 'עליות מנחה',
    values: ['עליה-מנחה-ראשון', 'עליה-מנחה-שני', 'עליה-מנחה-שלישי'],
  },
  {
    id: 'tefila',
    label: 'תפילות',
    values: ['תפילה', 'תפילה-שחרית', 'תפילה-מוסף', 'תפילה-מנחה', 'תפילה-ערבית', 'נעילה'],
  },
  {
    id: 'holidays',
    label: 'ימים נוראים וחגים',
    values: [
      'סליחות-שחרית', 'סליחות-מוסף', 'סליחות-מנחה', 'סליחות-ערבית', 'הפטרת-יונה',
      'כתר-תורה', 'חתן-תורה', 'מפטיר-שחרית', 'הקפות', 'בר-יוחאי', 'לך-אלי',
    ],
  },
  {
    id: 'honors',
    label: 'כיבודים ואירועים',
    values: ['מנורה', 'ברכה', 'ברכה-לרבים', 'אזכרה', 'סעודה שלישית', 'ארוע', 'ספר'],
  },
  {
    id: 'payments',
    label: 'תשלומים ותרומות',
    values: ['תשלום', 'תרומה', 'עמותה', 'ביטוח ספר תורה', 'זיכוי'],
  },
];

export const OTHER_ORDER_GROUP: OrderOptionGroup = { id: 'other', label: 'אחר', values: [] };

/** OrderOptions arranged by group, in group order; unlisted options go to "אחר". */
export function groupedOrderOptions(): { group: OrderOptionGroup; options: OrderOption[] }[] {
  const byValue = new Map(OrderOptions.map((o) => [o.value, o]));
  const seen = new Set<string>();
  const out = ORDER_OPTION_GROUPS.map((group) => ({
    group,
    options: group.values.flatMap((v) => {
      const o = byValue.get(v);
      if (!o || seen.has(v)) return [];
      seen.add(v);
      return [o];
    }),
  }));
  const rest = OrderOptions.filter((o) => !seen.has(o.value));
  if (rest.length) out.push({ group: OTHER_ORDER_GROUP, options: rest });
  return out;
}

// ─── Aliyot canonical order (for print sheet generation) ──────────────────

export const ALIYA_PRINT_ORDER: readonly string[] = [
  'פתיחת היכל',
  'עליה-שלישי',
  'עליה-רביעי',
  'עליה-חמישי',
  'עליה-שישי',
  'עליה-שביעי',
  'מפטיר',
  'תרגום',
];

// ─── Synagogue identity (for headers, prints) ─────────────────────────────

export const SYNAGOGUE_NAME_HEB = 'תפארת ישראל';
export const SYNAGOGUE_LOCATION_HEB = 'גני צבי';

// ─── Balance adjustments (admin overrides) ────────────────────────────────
//
// Used when an admin manually corrects a user's balance (e.g. to fix a
// historical off-by-one from the 20→19 input bug). Stored as a regular
// order document with this marker on both `name[0]` and `parasha` so
// client code can identify and render them as readonly history entries.
//
// The math still flows through the standard ledger:
//   for a +N adjustment  → price=0,        pricePaid=N    → balance impact = +N
//   for a -N adjustment  → price=N,        pricePaid=0    → balance impact = -N

export const ADJUSTMENT_MARKER = 'התאמת יתרה';

export function isAdjustmentOrder(order: { name?: string[]; parasha?: string }): boolean {
  return (
    order.parasha === ADJUSTMENT_MARKER ||
    (Array.isArray(order.name) && order.name[0] === ADJUSTMENT_MARKER)
  );
}
